/**
 * sessions.ts — draw the SESSION BOXES and the Asia model. Compute nothing.
 *
 * Card `t_5b2017b5`. Every number arrives from `/api/overlays/{a}/{tf}/sessions`, which is
 * `ict.session_model` (the windows, the rule and their timecodes live in that module's
 * docstring). This file maps rows to shapes and decides no rule — `overlays.ts`' standing
 * contract: geometry in, pixels out.
 *
 *   boxes      one band per CLOSED session window (Asia 19-02 / Frankfurt 02-03 / London 03-08 /
 *              NY 08-11 ET), from its first bar to its last, at the window's high and low.
 *              `exactSpan`: the width is the window, never the readability floor.
 *   target     a 1px band at the opposite Asia boundary, from the manipulation bar to the bar it
 *              was reached on (D), or to where the miss became known, or to the right edge while
 *              still open. A `both` manipulation names no side, so it has no target.
 *   markers    `M` on the bar that first took an Asia boundary, `D` on the bar that touched the
 *              target. At most two per trading day, so labelling them does not pile up the way
 *              per-fact markers did (`ltfevidence.ts` header).
 *
 * NOT A SIGNAL: no entry, no stop, no size. The author leaves both UNSPECIFIED, so none is drawn.
 */
import type { SeriesMarker, Time, UTCTimestamp } from 'lightweight-charts';
import type { ZoneBand } from './overlays';

export const SESSION_NAMES = ['asia', 'frankfurt', 'london', 'ny'] as const;
export type SessionName = (typeof SESSION_NAMES)[number];

export type SessionBox = {
  session: SessionName;
  trading_day: string;
  time: UTCTimestamp;
  end_time: UTCTimestamp;
  knowable_time: UTCTimestamp;
  high: number;
  low: number;
  n_bars: number;
};

export type SessionManipulation = {
  trading_day: string;
  time: UTCTimestamp;
  /** Where the day's model resolved (its D or `not_reached` miss); null while the target is
   *  open. The server keeps the row while this is on the pane (`main._trim_by_time`). */
  end_time: UTCTimestamp | null;
  knowable_time: UTCTimestamp;
  side: 'high' | 'low' | 'both';
  session: SessionName | null;
  extreme: number | null;
  asia_high: number;
  asia_low: number;
  target: number | null;
  target_boundary: 'asia_high' | 'asia_low' | null;
};

export type SessionDistribution = {
  trading_day: string;
  time: UTCTimestamp;
  from_time: UTCTimestamp;
  price: number;
};

export type SessionMiss = {
  trading_day: string;
  kind: 'no_manipulation' | 'not_reached';
  time: UTCTimestamp;
};

export type SessionsPayload = {
  component: 'sessions';
  source: string;
  unsupported_tf: string | null;
  boxes: SessionBox[];
  manipulations: SessionManipulation[];
  distributions: SessionDistribution[];
  misses: SessionMiss[];
};

/** Fill / border / caption per window. Asia carries the model, so it is the strongest box. */
const STYLE: Record<SessionName, { fill: string; border: string; label: string }> = {
  asia: { fill: 'rgba(255, 193, 7, 0.10)', border: 'rgba(255, 193, 7, 0.75)', label: 'Asia' },
  frankfurt: { fill: 'rgba(156, 39, 176, 0.08)', border: 'rgba(156, 39, 176, 0.55)', label: 'FR' },
  london: { fill: 'rgba(33, 150, 243, 0.07)', border: 'rgba(33, 150, 243, 0.55)', label: 'LN' },
  ny: { fill: 'rgba(76, 175, 80, 0.07)', border: 'rgba(76, 175, 80, 0.55)', label: 'NY' },
};

export const TARGET_COLOR = 'rgba(255, 193, 7, 0.95)';

/** A box that began before the pane's first bar starts at that bar instead: `RangePrimitive`
 *  draws NOTHING for a band anchored before the series (`_at` -> null), and the server keeps such
 *  a box precisely because part of it is on screen (`main._trim_by_time`, `end_time`). Its high
 *  and low are still the whole window's -- only the off-pane part of the rectangle is cut.
 *  `idmLevels` clamps for the same reason (card `t_79d313f6`). */
const clamp = (t: UTCTimestamp, first: number | null): UTCTimestamp =>
  (first != null && t < first ? first : t) as UTCTimestamp;

export function sessionBands(p: SessionsPayload, windowFirstTime: number | null = null): ZoneBand[] {
  return p.boxes.map((b) => ({
    lo: b.low,
    hi: b.high,
    fromTime: clamp(b.time, windowFirstTime),
    toTime: b.end_time,
    fill: STYLE[b.session].fill,
    border: STYLE[b.session].border,
    label: STYLE[b.session].label,
    exactSpan: true,
  }));
}

/** The opposite-boundary target, from M to wherever the day's model ended. An M left of the pane
 *  (kept by the server while its day is on screen or open) starts at the pane's first bar, for
 *  the same `RangePrimitive` reason as the boxes (quant r1 on this card). */
export function targetBands(p: SessionsPayload, windowFirstTime: number | null = null): ZoneBand[] {
  const reached = new Map(p.distributions.map((d) => [d.trading_day, d.time]));
  const missed = new Map(
    p.misses.filter((m) => m.kind === 'not_reached').map((m) => [m.trading_day, m.time]),
  );
  const out: ZoneBand[] = [];
  for (const m of p.manipulations) {
    if (m.target == null) continue;
    const end = reached.get(m.trading_day) ?? missed.get(m.trading_day) ?? null;
    out.push({
      lo: m.target,
      hi: m.target,
      fromTime: clamp(m.time, windowFirstTime),
      toTime: end,
      fill: TARGET_COLOR,
      border: TARGET_COLOR,
      label: m.target_boundary === 'asia_low' ? 'target: Asia low' : 'target: Asia high',
      exactSpan: true,
    });
  }
  return out;
}

/** M is NOT drawn when its bar is left of the pane: there is no bar to put it on, and moving it
 *  to the first bar would date the boundary-take wrongly. The row still names D's side. */
export function sessionMarkers(
  p: SessionsPayload,
  windowFirstTime: number | null = null,
): SeriesMarker<Time>[] {
  const out: SeriesMarker<Time>[] = [];
  for (const m of p.manipulations) {
    if (windowFirstTime != null && m.time < windowFirstTime) continue;
    out.push({
      time: m.time,
      position: m.side === 'low' ? 'belowBar' : 'aboveBar',
      color: TARGET_COLOR,
      shape: m.side === 'low' ? 'arrowUp' : 'arrowDown',
      text: 'M',
    });
  }
  const side = new Map(p.manipulations.map((m) => [m.trading_day, m.side]));
  for (const d of p.distributions) {
    // The target is BELOW when the high was taken, so D sits under the bar, and vice versa.
    const below = side.get(d.trading_day) === 'high';
    out.push({
      time: d.time,
      position: below ? 'belowBar' : 'aboveBar',
      color: TARGET_COLOR,
      shape: 'circle',
      text: 'D',
    });
  }
  return out.sort((a, b) => (a.time as number) - (b.time as number));
}

export function sessionsSummary(p: SessionsPayload): [string, string][] {
  if (p.unsupported_tf) return [['sessions', p.unsupported_tf]];
  const miss = (k: SessionMiss['kind']) => p.misses.filter((m) => m.kind === k).length;
  return [
    ['sessions', `${p.boxes.length} boxes (Asia 19-02 / FR 02-03 / LN 03-08 / NY 08-11 ET)`],
    ['what this is', 'his Asia model, closed bars only — NOT an entry signal'],
    ['Asia boundary taken (M)', String(p.manipulations.length)],
    ['target reached (D)', String(p.distributions.length)],
    ['target not reached by 11:00', String(miss('not_reached'))],
    ['no boundary taken by 11:00', String(miss('no_manipulation'))],
  ];
}

/** ONE CALL for every shape and the rows describing them (`setupDrawing`'s shape). */
export function sessionsDrawing(p: SessionsPayload, windowFirstTime: number | null = null): {
  bands: ZoneBand[];
  markers: SeriesMarker<Time>[];
  rows: [string, string][];
} {
  return {
    bands: [...sessionBands(p, windowFirstTime), ...targetBands(p, windowFirstTime)],
    markers: sessionMarkers(p, windowFirstTime),
    rows: sessionsSummary(p),
  };
}
